import {
  DATA_DICTIONARY_CATALOG_OPTION_ID,
  GLOBAL_ASSISTANT_PAGE_CATALOG,
} from './catalog-data';
import type { DashboardEntity, GlobalAssistantPageCatalogEntry } from './types';

export { GLOBAL_ASSISTANT_PAGE_CATALOG } from './catalog-data';
export type {
  DashboardEntity,
  GlobalAssistantPageCatalogEntry,
} from './types';

const getEnabledPageCatalogEntries =
  (): readonly GlobalAssistantPageCatalogEntry[] =>
    GLOBAL_ASSISTANT_PAGE_CATALOG.filter((entry) => entry.enabled);

export const getPageCatalogEntry = (
  optionId: string
): GlobalAssistantPageCatalogEntry | undefined =>
  GLOBAL_ASSISTANT_PAGE_CATALOG.find(
    (entry) => entry.optionId === optionId && entry.enabled
  );

const dataDictionaryPageEntry = GLOBAL_ASSISTANT_PAGE_CATALOG.find(
  (entry) =>
    entry.enabled && entry.optionId === DATA_DICTIONARY_CATALOG_OPTION_ID
);

if (!dataDictionaryPageEntry) {
  throw new Error(
    'Global Assistant page catalog must contain an enabled Data Dictionary entry'
  );
}

/** Canonical navigation option id for the Data Dictionary catalog entry. */
export const DATA_DICTIONARY_PAGE_OPTION_ID = dataDictionaryPageEntry.optionId;

/**
 * Option id → route for the pages whose route is not derivable from the id.
 * Both directions of that mapping have to agree (the id builds the link, the
 * link builds the button label), so both read this one map rather than
 * re-listing the exception. `page-catalog-navigation-parity.spec.ts` pins the
 * result against the routes the product nav actually serves.
 */
export const PAGE_NAVIGATE_PATH_OVERRIDES: ReadonlyMap<string, string> =
  new Map(
    getEnabledPageCatalogEntries().flatMap((entry) =>
      entry.navigatePathOverride
        ? [[entry.optionId, entry.navigatePathOverride] as const]
        : []
    )
  );

const formatPageBlock = (entry: GlobalAssistantPageCatalogEntry): string => {
  const outcomes = entry.capabilityModel.outcomes
    .map((o) => `    - ${o}`)
    .join('\n');
  const bestFor = entry.capabilityModel.bestForExamples
    .map((e) => `    - ${e}`)
    .join('\n');
  const notFor = entry.capabilityModel.notFor
    .map((n) => `    - ${n}`)
    .join('\n');
  const cues = entry.capabilityModel.selectionCues?.length
    ? `\n  Selection cues:\n${entry.capabilityModel.selectionCues
        .map((c) => `    - ${c}`)
        .join('\n')}`
    : '';
  return `- ${entry.optionId} (${entry.pageName}):
  Outcomes:
${outcomes}
  Best for:
${bestFor}
  Not for:
${notFor}${cues}`;
};

/**
 * Rich "## Pages you can open" capability digest for the loop system prompt.
 * `excludeOptionIds` omits pages the account cannot access (mirrors
 * canonicalIntentsSummaryForSystemPrompt's access filtering).
 */
export const buildPageCapabilityDigest = (
  excludeOptionIds?: ReadonlySet<string>
): string =>
  getEnabledPageCatalogEntries()
    .filter((entry) => !excludeOptionIds?.has(entry.optionId))
    .map(formatPageBlock)
    .join('\n\n');

/** `__std__:company:compositions` → `compositions`. */
type SegmentOfOptionId<T extends string> =
  T extends `__std__:${string}:${infer Segment}` ? Segment : never;

/**
 * Every URL segment the catalog describes a page for. Derived, so a domain or a
 * label keyed on a segment that does not exist is a COMPILE error.
 */
export type StandardPageSegment = SegmentOfOptionId<
  (typeof GLOBAL_ASSISTANT_PAGE_CATALOG)[number]['optionId']
>;

const segmentOf = (optionId: string): string => optionId.split(':')[2] ?? '';

const ENABLED_ENTITY_SEGMENT_PAIRS: ReadonlySet<string> = new Set(
  GLOBAL_ASSISTANT_PAGE_CATALOG.filter((entry) => entry.enabled).map(
    (entry) => `${entry.entity}:${segmentOf(entry.optionId)}`
  )
);

/**
 * Whether the assistant may open this page for this entity. The catalog lists
 * the pages the assistant routes to: `page-catalog-nav-parity.spec` pins its
 * analytics pairs to the nav, and `page-domains.spec` keeps it within
 * `ENTITY_PAGE_DOMAINS`, the product's own page record. Path building must
 * consult this instead of concatenating `/${entity}/${segment}`: Market
 * Conditions, for example, exists for every entity but company, and
 * Transitions for neither skill nor activity, so concatenation would emit a
 * route that does not exist.
 */
export const isStandardPagePair = (
  entity: DashboardEntity,
  segment: StandardPageSegment
): boolean => ENABLED_ENTITY_SEGMENT_PAIRS.has(`${entity}:${segment}`);

/**
 * Metric domains an answer can offer a standard page for — a strict subset of
 * the catalog's segments (`satisfies` makes a domain naming a non-existent page
 * a compile error). `screener` is deliberately absent: a discovery surface, not
 * a metric domain. `summary` is absent because the Overview pages were removed
 * from the layout in #6036.
 */
const ASSISTANT_DOMAINS = [
  'compositions',
  'transitions',
  'postings',
  'pay',
  'sentiment',
  'market-conditions',
] as const satisfies readonly StandardPageSegment[];

export type AssistantDomain = (typeof ASSISTANT_DOMAINS)[number];
